/**
 * Akeneo Tool - Modular TypeScript Frontend App
 */

import { state } from './state.js';
import {
  detectIframeMode,
  applyEmbeddingUI,
  toggleEmbeddingMode,
  switchTab,
  initRouter,
  fetchHealth,
  checkUrlNotifications,
} from './controllers/app.controller.js';
import {
  fetchConnections,
  updateHeaderAndOverviewMetrics,
  renderActivePair,
  renderConnectionsList,
  handleAuthTypeChange,
  editConnection,
  resetConnectionForm,
  collectFormPayload,
  testConnectionForm,
  testConnectionById,
  handleSaveConnection,
  activateConnection,
  openDeleteModal,
  closeDeleteModal,
  confirmDeleteConnection,
  openOAuthModal,
  closeOAuthModal,
  startOAuthFlow,
  scrollToForm,
} from './controllers/connections.controller.js';
import {
  switchLogsSubTab,
  startLogsLivePolling,
  stopLogsLivePolling,
  toggleLivePolling,
  fetchJobStats,
  fetchJobs,
  renderJobsTable,
  openJobDetailModal,
  fetchJobAuditLogs,
  closeJobDetailModal,
  cancelJob,
  deleteJob,
  createSampleJob,
  fetchAuditLogs,
  renderAuditLogs,
  renderErrorDiagnostics,
  retryJob,
  retrySingleEntityItem,
  retryAllFailedEntities,
  exportLogs,
  openClearLogsModal,
  closeClearLogsModal,
  confirmClearLogs,
} from './controllers/logs.controller.js';
import {
  CANONICAL_POLLING_TYPES,
  fetchMonitoringData,
  renderMonitoringOverview,
  renderWebhookEvents,
  renderPollingCheckpoints,
  renderLastPollResult,
  renderQueueAndDlq,
  startRealtimeWorker,
  stopRealtimeWorker,
  pauseRealtimeWorker,
  resetRealtimeWorkerStats,
  startPollingWorker,
  stopPollingWorker,
  pausePollingWorker,
  triggerPollingRun,
  openPollTriggerModal,
  closePollTriggerModal,
  confirmPollTrigger,
  openCheckpointEditModal,
  closeCheckpointEditModal,
  saveCheckpoint,
  saveCheckpointFromModal,
  setAllCheckpoints,
  resetAllCheckpoints,
  flushWebhookQueue,
  toggleQueuePause,
  clearWebhookQueueBuffer,
  retryDlqItem,
  retryAllDlq,
  clearDlq,
  clearWebhookEventsFeed,
  resetWebhookStats,
  openEventDetailModal,
  closeEventDetailModal,
  syncEntityFromEvent,
  simulateWebhookEvent,
  executeOnDemandSync,
  switchMonitoringSubTab,
  startMonitoringLivePolling,
  stopMonitoringLivePolling,
  toggleMonitoringLivePolling,
} from './controllers/monitoring.controller.js';
import {
  ENTITY_TYPE_METADATA,
  fetchBatchInitialData,
  switchBatchSubTab,
  fetchDiscoverySummary,
  fetchCategoryTree,
  countTreeNodes,
  renderCategoryTree,
  filterCategoryTreeNode,
  selectCategoryFilter,
  fetchCatalogFilters,
  fetchCatalogItems,
  renderCatalogTable,
  renderCatalogPagination,
  renderActiveFilterChips,
  toggleCatalogSelection,
  selectAllCatalogItems,
  clearCatalogSelection,
  updateSelectionBar,
  executeSelectiveSync,
  executeSelectiveDryRun,
  inspectCatalogEntity,
  renderEntityTypesGrid,
  toggleEntityTypeSelection,
  setEntityTypePreset,
  updateTopologicalOrder,
  checkBatchReadiness,
  runPreFlightValidation,
  runDryRunDiffPreview,
  renderValidationSummary,
  renderDiffInspector,
  filterDiffItems,
  executeBatchSyncJob,
  openBatchProgressModal,
  updateBatchProgressModal,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  getPruneOptions,
  setPruneEnabled,
  setPruneThreshold,
  setForceDelete,
} from './controllers/batch.controller.js';
import {
  EXPLORER_ENTITY_TYPES,
  fetchExplorerItems,
  initExplorer,
  inspectExplorerEntity,
  compareExplorerEntity,
  closeEntityDiffModal,
  executeEntityDiffSync,
  isProductLikeEntityType,
  renderExplorerEntitySelector,
  renderExplorerPagination,
  renderExplorerTable,
  resetExplorerFilters,
  setExplorerEnabledFilter,
  setExplorerEntityType,
  setExplorerPage,
  setExplorerPageSize,
  setExplorerSearch,
  syncExplorerItem,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  deleteExplorerItem,
  toggleExplorerItemSelection,
  selectAllExplorerItems,
  clearExplorerSelection,
  openBatchDeleteModal,
  closeBatchDeleteModal,
  confirmBatchDelete,
  // Task 16.4: Entity Browser UI Integration (Add to Virtual Target Action & Modal)
  openAddToVirtualTargetModal,
  closeAddToVirtualTargetModal,
  confirmAddToVirtualTarget,
  setExplorerTargetMode,
} from './controllers/explorer.controller.js';
import {
  initStaging,
  fetchStagingPackages,
  createStagingPackage,
  selectStagingPackage,
  fetchStagingItems,
  addStagingItem,
  removeStagingItem,
  setStagingCurateField,
  setStagingDeployField,
  switchStagingSubTab,
  runStagingResolve,
  freezeStagingPackage,
  unfreezeStagingPackage,
  openEditStagingPackageModal,
  closeEditStagingPackageModal,
  saveEditStagingPackage,
  updateStagingReleaseActionsUI,
  exportStagingPackage,
  openDeployStagingModal,
  closeDeployStagingModal,
  deployStagingPackage,
  fetchStagingTargetReadiness,
  renderStagingReadinessScorecard,
  runStagingDryRunPreview,
  renderStagingDryRunPreview,
  updateStagingDeployProgress,
  addStagingDeployLog,
  switchStagingDeployView,
  renderStagingPackages,
  renderStagingItems,
  renderStagingGraph,
  renderStagingTargetSelect,
  // Task 16.9: Scenario Presets Wizard (Company Setup, Product Batch, Backup)
  SCENARIO_PRESETS,
  initScenarioWizard,
  selectScenarioPreset,
  renderScenarioPresetCards,
  renderScenarioConfigPanel,
  resetScenarioPreset,
  updateScenarioWizardSubmitButton,
  fetchScenarioOptions,
  setStagingScenarioFilter,
  populateVirtualTargetFromScenario,
  setStagingFilterType,
  setStagingSearch,
  setStagingPage,
  setStagingLimit,
  renderStagingCategoryTabs,
  renderStagingPagination,
  // Task 16.6: File Import UI in Staging Tab
  setStagingImportFile,
  setStagingImportField,
  resetStagingImport,
  updateStagingImportUI,
  handleStagingDragOver,
  handleStagingDragLeave,
  handleStagingDrop,
  handleStagingFileSelect,
  importStagingPackageFile,
  renderStagingImportTargetSelect,
  // Task 16.8: Staging Entity Detail Inspector & Payload Editor & Bulk Deletion
  inspectStagingItem,
  closeStagingItemInspectModal,
  switchStagingInspectTab,
  setStagingPayloadDraft,
  validateStagingPayloadDraft,
  formatStagingPayloadDraft,
  saveStagingItemPayload,
  deleteStagingItemFromInspector,
  toggleStagingItemSelection,
  selectAllStagingItems,
  clearStagingSelection,
  openStagingBulkDeleteModal,
  closeStagingBulkDeleteModal,
  confirmStagingBulkDelete,
  bulkDeleteStagingItems,
  updateStagingBulkActionsUI,
  renderStagingFormattedValues,
  renderStagingItemDependencies,
} from './controllers/staging.controller.js';
import {
  initIframeAuth,
  requestJwt,
  handleParentMessage,
  exchangeJwtToken,
  checkAuthSession,
  logoutUser,
  getAuthHeader,
  renderAuthUI,
} from './controllers/iframe-auth.js';
import {
  fetchBackupSummary,
  openBackupModal,
  closeBackupModal,
  exportBackup,
  importBackup,
  setBackupScope,
  setBackupFormat,
  setBackupDryRun,
  handleBackupDragOver,
  handleBackupDragLeave,
  handleBackupDrop,
  handleBackupFileSelect,
  preflightRestore,
  confirmRestore,
  resetRestoreState,
} from './controllers/backup.controller.js';
import {
  initComparison,
  fetchComparisonReports,
  renderComparisonForm,
  renderComparisonSummary,
  renderComparisonEntityGroups,
  setComparisonSide,
  toggleComparisonEntityType,
  selectAllComparisonEntityTypes,
  runComparison,
  loadComparisonReport,
  toggleComparisonTypeExpansion,
  openComparisonEntityDiff,
  deleteComparisonReport,
  renderComparisonDiffModal,
  closeComparisonDiffModal,
  executeComparisonDiffSync,
  openComparisonEntityAddToPackage,
  setComparisonEntityTypeFilter,
  setComparisonEntityTypePage,
  setComparisonEntityTypePageSize,
  setComparisonReportFilter,
} from './controllers/comparison.controller.js';
import { initEvents } from './events.js';
import { showToast } from './utils/toast.js';
import { initSSE, getSSEClient, SSEClient } from './sse.js';
import {
  formatUptime,
  escapeHtml,
  formatAuthType,
  formatDuration,
  formatDateTime,
  formatRelativeTime,
} from './utils/formatters.js';
import { AkeneoDashboardGlobal } from './types.js';

// Export all modules
export {
  state,
  detectIframeMode,
  applyEmbeddingUI,
  toggleEmbeddingMode,
  switchTab,
  initRouter,
  fetchHealth,
  checkUrlNotifications,
  fetchConnections,
  updateHeaderAndOverviewMetrics,
  renderActivePair,
  renderConnectionsList,
  handleAuthTypeChange,
  editConnection,
  resetConnectionForm,
  collectFormPayload,
  testConnectionForm,
  testConnectionById,
  handleSaveConnection,
  activateConnection,
  openDeleteModal,
  closeDeleteModal,
  confirmDeleteConnection,
  openOAuthModal,
  closeOAuthModal,
  startOAuthFlow,
  scrollToForm,
  switchLogsSubTab,
  startLogsLivePolling,
  stopLogsLivePolling,
  toggleLivePolling,
  fetchJobStats,
  fetchJobs,
  renderJobsTable,
  openJobDetailModal,
  fetchJobAuditLogs,
  closeJobDetailModal,
  cancelJob,
  deleteJob,
  createSampleJob,
  fetchAuditLogs,
  renderAuditLogs,
  renderErrorDiagnostics,
  retryJob,
  retrySingleEntityItem,
  retryAllFailedEntities,
  exportLogs,
  openClearLogsModal,
  closeClearLogsModal,
  confirmClearLogs,
  CANONICAL_POLLING_TYPES,
  fetchMonitoringData,
  renderMonitoringOverview,
  renderWebhookEvents,
  renderPollingCheckpoints,
  renderLastPollResult,
  renderQueueAndDlq,
  startRealtimeWorker,
  stopRealtimeWorker,
  pauseRealtimeWorker,
  resetRealtimeWorkerStats,
  startPollingWorker,
  stopPollingWorker,
  pausePollingWorker,
  triggerPollingRun,
  openPollTriggerModal,
  closePollTriggerModal,
  confirmPollTrigger,
  openCheckpointEditModal,
  closeCheckpointEditModal,
  saveCheckpoint,
  saveCheckpointFromModal,
  setAllCheckpoints,
  resetAllCheckpoints,
  flushWebhookQueue,
  toggleQueuePause,
  clearWebhookQueueBuffer,
  retryDlqItem,
  retryAllDlq,
  clearDlq,
  clearWebhookEventsFeed,
  resetWebhookStats,
  openEventDetailModal,
  closeEventDetailModal,
  syncEntityFromEvent,
  simulateWebhookEvent,
  executeOnDemandSync,
  switchMonitoringSubTab,
  startMonitoringLivePolling,
  stopMonitoringLivePolling,
  toggleMonitoringLivePolling,
  ENTITY_TYPE_METADATA,
  fetchBatchInitialData,
  switchBatchSubTab,
  fetchDiscoverySummary,
  fetchCategoryTree,
  countTreeNodes,
  renderCategoryTree,
  filterCategoryTreeNode,
  selectCategoryFilter,
  fetchCatalogFilters,
  fetchCatalogItems,
  renderCatalogTable,
  renderCatalogPagination,
  renderActiveFilterChips,
  toggleCatalogSelection,
  selectAllCatalogItems,
  clearCatalogSelection,
  updateSelectionBar,
  executeSelectiveSync,
  executeSelectiveDryRun,
  inspectCatalogEntity,
  renderEntityTypesGrid,
  toggleEntityTypeSelection,
  setEntityTypePreset,
  updateTopologicalOrder,
  checkBatchReadiness,
  runPreFlightValidation,
  runDryRunDiffPreview,
  renderValidationSummary,
  renderDiffInspector,
  filterDiffItems,
  executeBatchSyncJob,
  openBatchProgressModal,
  updateBatchProgressModal,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  getPruneOptions,
  setPruneEnabled,
  setPruneThreshold,
  setForceDelete,
  // Task 14.1: Source Entity Explorer Navigation & Multi-Entity Browser UI
  EXPLORER_ENTITY_TYPES,
  fetchExplorerItems,
  initExplorer,
  inspectExplorerEntity,
  compareExplorerEntity,
  closeEntityDiffModal,
  executeEntityDiffSync,
  isProductLikeEntityType,
  renderExplorerEntitySelector,
  renderExplorerPagination,
  renderExplorerTable,
  resetExplorerFilters,
  setExplorerEnabledFilter,
  setExplorerEntityType,
  setExplorerPage,
  setExplorerPageSize,
  setExplorerSearch,
  syncExplorerItem,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  deleteExplorerItem,
  toggleExplorerItemSelection,
  selectAllExplorerItems,
  clearExplorerSelection,
  openBatchDeleteModal,
  closeBatchDeleteModal,
  confirmBatchDelete,
  // Task 16.4: Entity Browser UI Integration (Add to Virtual Target Action & Modal)
  openAddToVirtualTargetModal,
  closeAddToVirtualTargetModal,
  confirmAddToVirtualTarget,
  setExplorerTargetMode,
  // Task 6.3: Staging & Release Package Builder UI
  initStaging,
  fetchStagingPackages,
  createStagingPackage,
  selectStagingPackage,
  fetchStagingItems,
  addStagingItem,
  removeStagingItem,
  setStagingCurateField,
  setStagingDeployField,
  switchStagingSubTab,
  runStagingResolve,
  freezeStagingPackage,
  unfreezeStagingPackage,
  openEditStagingPackageModal,
  closeEditStagingPackageModal,
  saveEditStagingPackage,
  updateStagingReleaseActionsUI,
  exportStagingPackage,
  openDeployStagingModal,
  closeDeployStagingModal,
  deployStagingPackage,
  fetchStagingTargetReadiness,
  renderStagingReadinessScorecard,
  runStagingDryRunPreview,
  renderStagingDryRunPreview,
  updateStagingDeployProgress,
  addStagingDeployLog,
  switchStagingDeployView,
  renderStagingPackages,
  renderStagingItems,
  renderStagingGraph,
  renderStagingTargetSelect,
  // Task 16.9: Scenario Presets Wizard (Company Setup, Product Batch, Backup)
  SCENARIO_PRESETS,
  initScenarioWizard,
  selectScenarioPreset,
  renderScenarioPresetCards,
  renderScenarioConfigPanel,
  resetScenarioPreset,
  updateScenarioWizardSubmitButton,
  fetchScenarioOptions,
  setStagingScenarioFilter,
  populateVirtualTargetFromScenario,
  // Task 16.7: Staging Entity Browser with Type Filter Tabs, Search & Pagination
  setStagingFilterType,
  setStagingSearch,
  setStagingPage,
  setStagingLimit,
  renderStagingCategoryTabs,
  renderStagingPagination,
  // Task 16.8: Staging Entity Detail Inspector & Payload Editor & Bulk Deletion
  inspectStagingItem,
  closeStagingItemInspectModal,
  switchStagingInspectTab,
  setStagingPayloadDraft,
  validateStagingPayloadDraft,
  formatStagingPayloadDraft,
  saveStagingItemPayload,
  deleteStagingItemFromInspector,
  toggleStagingItemSelection,
  selectAllStagingItems,
  clearStagingSelection,
  openStagingBulkDeleteModal,
  closeStagingBulkDeleteModal,
  confirmStagingBulkDelete,
  bulkDeleteStagingItems,
  updateStagingBulkActionsUI,
  renderStagingFormattedValues,
  renderStagingItemDependencies,
  // Task 13.2: Configuration Backup, Export & Import Profile Bundles
  fetchBackupSummary,
  openBackupModal,
  closeBackupModal,
  exportBackup,
  importBackup,
  // Task: Frontend comparison report & summary view
  initComparison,
  fetchComparisonReports,
  renderComparisonForm,
  renderComparisonSummary,
  renderComparisonEntityGroups,
  setComparisonSide,
  toggleComparisonEntityType,
  selectAllComparisonEntityTypes,
  runComparison,
  loadComparisonReport,
  toggleComparisonTypeExpansion,
  openComparisonEntityDiff,
  deleteComparisonReport,
  renderComparisonDiffModal,
  closeComparisonDiffModal,
  executeComparisonDiffSync,
  openComparisonEntityAddToPackage,
  setComparisonEntityTypeFilter,
  setComparisonEntityTypePage,
  setComparisonEntityTypePageSize,
  setComparisonReportFilter,
  // Task 11.3: Real-Time SSE Stream Exports
  initEvents,
  showToast,
  formatUptime,
  escapeHtml,
  formatAuthType,
  formatDuration,
  formatDateTime,
  formatRelativeTime,
};

// Global AkeneoDashboard object for window bindings
export const dashboard: AkeneoDashboardGlobal = {
  state,
  switchTab,
  toggleEmbeddingMode,
  fetchHealth,
  fetchConnections,
  testConnectionForm,
  testStoredConnection: testConnectionById,
  activateConnection,
  editConnection,
  resetConnectionForm,
  scrollToForm,
  openDeleteModal,
  closeDeleteModal,
  confirmDeleteConnection,
  openOAuthModal,
  closeOAuthModal,
  startOAuthFlow,
  showToast,
  // Task 9.4: History & Logs Exports
  fetchJobs,
  fetchAuditLogs,
  fetchJobStats,
  renderJobsTable,
  renderAuditLogs,
  renderErrorDiagnostics,
  openJobDetail: openJobDetailModal,
  closeJobDetail: closeJobDetailModal,
  cancelJob,
  deleteJob,
  createSampleJob,
  switchLogsSubTab,
  startLogsLivePolling,
  stopLogsLivePolling,
  toggleLivePolling,
  exportLogs,
  openClearLogsModal,
  closeClearLogsModal,
  confirmClearLogs,
  // Task 10.2: Error Recovery & Partial Retry Exports
  retryJob,
  retrySingleItem: retrySingleEntityItem,
  retrySingleEntityItem,
  retryAllFailedEntities,
  // Task 9.5: Continuous Sync & Monitoring Exports
  fetchMonitoringData,
  renderMonitoringOverview,
  renderWebhookEvents,
  renderPollingCheckpoints,
  renderLastPollResult,
  renderQueueAndDlq,
  startRealtimeWorker,
  stopRealtimeWorker,
  pauseRealtimeWorker,
  resetRealtimeWorkerStats,
  startPollingWorker,
  stopPollingWorker,
  pausePollingWorker,
  triggerPollingRun,
  openPollTriggerModal,
  closePollTriggerModal,
  confirmPollTrigger,
  openCheckpointEditModal,
  closeCheckpointEditModal,
  saveCheckpoint,
  saveCheckpointFromModal,
  setAllCheckpoints,
  resetAllCheckpoints,
  flushWebhookQueue,
  toggleQueuePause,
  clearWebhookQueueBuffer,
  retryDlqItem,
  retryAllDlq,
  clearDlq,
  clearWebhookEventsFeed,
  resetWebhookStats,
  openEventDetailModal,
  closeEventDetailModal,
  syncEntityFromEvent,
  simulateWebhookEvent,
  executeOnDemandSync,
  // Task 9.3: Batch Sync & Catalog Browser Exports
  fetchBatchInitialData,
  switchBatchSubTab,
  fetchDiscoverySummary,
  fetchCategoryTree,
  renderCategoryTree,
  filterCategoryTreeNode,
  selectCategoryFilter,
  fetchCatalogFilters,
  fetchCatalogItems,
  renderCatalogTable,
  toggleCatalogSelection,
  selectAllCatalogItems,
  clearCatalogSelection,
  executeSelectiveSync,
  executeSelectiveDryRun,
  inspectCatalogEntity,
  renderEntityTypesGrid,
  setEntityTypePreset,
  toggleEntityTypeSelection,
  updateTopologicalOrder,
  checkBatchReadiness,
  runPreFlightValidation,
  runDryRunDiffPreview,
  renderValidationSummary,
  renderDiffInspector,
  filterDiffItems,
  executeBatchSyncJob,
  openBatchProgressModal,
  updateBatchProgressModal,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  getPruneOptions,
  setPruneEnabled,
  setPruneThreshold,
  setForceDelete,
  // Task 14.1: Source Entity Explorer Navigation & Multi-Entity Browser UI
  EXPLORER_ENTITY_TYPES,
  fetchExplorerItems,
  initExplorer,
  inspectExplorerEntity,
  compareExplorerEntity,
  closeEntityDiffModal,
  executeEntityDiffSync,
  isProductLikeEntityType,
  renderExplorerEntitySelector,
  renderExplorerPagination,
  renderExplorerTable,
  resetExplorerFilters,
  setExplorerEnabledFilter,
  setExplorerEntityType,
  setExplorerPage,
  setExplorerPageSize,
  setExplorerSearch,
  syncExplorerItem,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  deleteExplorerItem,
  toggleExplorerItemSelection,
  selectAllExplorerItems,
  clearExplorerSelection,
  openBatchDeleteModal,
  closeBatchDeleteModal,
  confirmBatchDelete,
  // Task 16.4: Entity Browser UI Integration (Add to Virtual Target Action & Modal)
  openAddToVirtualTargetModal,
  closeAddToVirtualTargetModal,
  confirmAddToVirtualTarget,
  setExplorerTargetMode,
  // Task 6.3: Staging & Release Package Builder UI
  initStaging,
  fetchStagingPackages,
  createStagingPackage,
  selectStagingPackage,
  fetchStagingItems,
  addStagingItem,
  removeStagingItem,
  setStagingCurateField,
  setStagingDeployField,
  switchStagingSubTab,
  runStagingResolve,
  freezeStagingPackage,
  unfreezeStagingPackage,
  openEditStagingPackageModal,
  closeEditStagingPackageModal,
  saveEditStagingPackage,
  updateStagingReleaseActionsUI,
  exportStagingPackage,
  openDeployStagingModal,
  closeDeployStagingModal,
  deployStagingPackage,
  fetchStagingTargetReadiness,
  renderStagingReadinessScorecard,
  runStagingDryRunPreview,
  renderStagingDryRunPreview,
  updateStagingDeployProgress,
  addStagingDeployLog,
  switchStagingDeployView,
  renderStagingPackages,
  renderStagingItems,
  renderStagingGraph,
  renderStagingTargetSelect,
  // Task 16.9: Scenario Presets Wizard (Company Setup, Product Batch, Backup)
  SCENARIO_PRESETS,
  initScenarioWizard,
  selectScenarioPreset,
  renderScenarioPresetCards,
  renderScenarioConfigPanel,
  resetScenarioPreset,
  updateScenarioWizardSubmitButton,
  fetchScenarioOptions,
  setStagingScenarioFilter,
  populateVirtualTargetFromScenario,
  // Task 16.7: Staging Entity Browser with Type Filter Tabs, Search & Pagination
  setStagingFilterType,
  setStagingSearch,
  setStagingPage,
  setStagingLimit,
  renderStagingCategoryTabs,
  renderStagingPagination,
  // Task 16.6: File Import UI in Staging Tab
  setStagingImportFile,
  setStagingImportField,
  resetStagingImport,
  updateStagingImportUI,
  handleStagingDragOver,
  handleStagingDragLeave,
  handleStagingDrop,
  handleStagingFileSelect,
  importStagingPackageFile,
  renderStagingImportTargetSelect,
  // Task 16.8: Staging Entity Detail Inspector & Payload Editor & Bulk Deletion
  inspectStagingItem,
  closeStagingItemInspectModal,
  switchStagingInspectTab,
  setStagingPayloadDraft,
  validateStagingPayloadDraft,
  formatStagingPayloadDraft,
  saveStagingItemPayload,
  deleteStagingItemFromInspector,
  toggleStagingItemSelection,
  selectAllStagingItems,
  clearStagingSelection,
  openStagingBulkDeleteModal,
  closeStagingBulkDeleteModal,
  confirmStagingBulkDelete,
  bulkDeleteStagingItems,
  updateStagingBulkActionsUI,
  renderStagingFormattedValues,
  renderStagingItemDependencies,
  // Task 6.4: Backup & Restore Manager UI (Dual-Format)
  setBackupScope,
  setBackupFormat,
  setBackupDryRun,
  handleBackupDragOver,
  handleBackupDragLeave,
  handleBackupDrop,
  handleBackupFileSelect,
  preflightRestore,
  confirmRestore,
  resetRestoreState,
  // Task 13.2: Configuration Backup, Export & Import Profile Bundles
  fetchBackupSummary,
  openBackupModal,
  closeBackupModal,
  exportBackup,
  importBackup,
  // Task: Frontend comparison report & summary view
  initComparison,
  fetchComparisonReports,
  renderComparisonForm,
  renderComparisonSummary,
  renderComparisonEntityGroups,
  setComparisonSide,
  toggleComparisonEntityType,
  selectAllComparisonEntityTypes,
  runComparison,
  loadComparisonReport,
  toggleComparisonTypeExpansion,
  openComparisonEntityDiff,
  deleteComparisonReport,
  renderComparisonDiffModal,
  closeComparisonDiffModal,
  executeComparisonDiffSync,
  openComparisonEntityAddToPackage,
  setComparisonEntityTypeFilter,
  setComparisonEntityTypePage,
  setComparisonEntityTypePageSize,
  setComparisonReportFilter,
  // Task 11.3: Real-Time SSE Stream Exports
  initSSE,
  getSSEClient,
  SSEClient,
  // Task 13.4: Akeneo Embedded App Iframe SSO & JWT Exports
  initIframeAuth,
  requestJwt,
  exchangeJwtToken,
  checkAuthSession,
  logoutUser,
  getAuthHeader,
  renderAuthUI,
};

if (typeof window !== 'undefined') {
  window.AkeneoDashboard = dashboard;

  let isDashboardInitialized = false;

  function initializeDashboard(): void {
    if (isDashboardInitialized) return;
    isDashboardInitialized = true;

    initRouter();
    initEvents();
    handleAuthTypeChange();
    fetchHealth();
    fetchConnections();
    checkUrlNotifications();
    initSSE();

    // Periodic polling every 10s
    if (!state.pollTimer) {
      state.pollTimer = setInterval(() => {
        fetchHealth();
        fetchConnections();
      }, 10000);
    }
  }

  /**
   * Application Bootstrap
   */
  document.addEventListener('DOMContentLoaded', async () => {
    detectIframeMode();

    // Listen for authentication success event from iframe SSO
    window.addEventListener('akeneo:authenticated', () => {
      initializeDashboard();
    });

    await initIframeAuth();

    // If bypass is active or already authenticated, initialize dashboard
    if (state.auth.authBypass || state.auth.isAuthenticated) {
      initializeDashboard();
    }
  });
}
